/**
 * Centralized configuration for pricing plans
 */

export interface PricingFeature {
  name: string;
  included: boolean;
}

export interface PricingPlan {
  id: string;
  title: string;
  description: string;
  price: string;
  originalPrice: string;
  priceDetail: string;
  monthlyAmount: number; // Actual amount in rupees for payment processing
  features: PricingFeature[];
  highlight?: boolean;
  badge?: string;
  computeCredits?: number; // New property for compute credits
  discountPercentage?: number; // New property for discount percentage
}

// Define the pricing plans with all their details (fallback if database is unavailable)
export const pricingPlans: Record<string, PricingPlan> = {
  basic: {
    id: 'basic',
    title: 'Basic',
    description: 'Perfect for getting started with AI',
    price: 'Rs. 250/mon',
    originalPrice: 'Rs. 750/mon',
    priceDetail: '',
    monthlyAmount: 250,
    computeCredits: 1000000,
    discountPercentage: 67,
    features: [
      { name: "1 million compute credits", included: true },
      { name: "15+ LLM models", included: true },
      { name: "Unified conversation history", included: true },
      { name: "PDF and image upload", included: true },
      { name: "Access to premium models", included: false },
      { name: "Early access to new models", included: false },
      { name: "Priority support", included: false },
    ],
    highlight: false,
  },
  starter: {
    id: 'starter',
    title: 'Starter',
    description: 'For regular AI users who need more power',
    price: 'Rs. 500/mon',
    originalPrice: 'Rs. 1,500/mon',
    priceDetail: '',
    monthlyAmount: 500,
    computeCredits: 2500000,
    discountPercentage: 67,
    features: [
      { name: "2.5 million compute credits", included: true },
      { name: "20+ LLM models", included: true },
      { name: "Unified conversation history", included: true },
      { name: "PDF and image upload", included: true },
      { name: "Access to premium models", included: true },
      { name: "Early access to new models", included: true },
      { name: "Priority support", included: false },
    ],
    highlight: true,
    badge: "Launch Special",
  },
  professional: {
    id: 'professional',
    title: 'Professional',
    description: 'For power users and professionals',
    price: 'Rs. 2,000/mon',
    originalPrice: 'Rs. 6,000/mon',
    priceDetail: '',
    monthlyAmount: 2000,
    computeCredits: 10000000,
    discountPercentage: 67,
    features: [
      { name: "10 million compute credits", included: true },
      { name: "20+ LLM models", included: true },
      { name: "Unlimited conversation history", included: true },
      { name: "PDF and image upload", included: true },
      { name: "Access to all premium models", included: true },
      { name: "Early access to new models", included: true },
      { name: "Priority support", included: true },
    ],
    highlight: false,
  }
};

// Helper function to get a plan by its ID (this is used as a fallback)
export const getPlanById = (planId: string): PricingPlan => {
  return pricingPlans[planId] || pricingPlans.basic;
};

// Get all plans as an array (this is used as a fallback)
export const getAllPlans = (): PricingPlan[] => {
  return Object.values(pricingPlans);
};

// Fallback model comparison data
export const modelComparisonData = [
  { modelName: 'Claude 3.5 Haiku', basic: true, starter: true, professional: true },
  { modelName: 'Claude 3.5 Sonnet', basic: true, starter: true, professional: true },
  { modelName: 'Claude 3.7 Sonnet', basic: false, starter: true, professional: true },
  { modelName: 'Claude 3 Opus', basic: false, starter: true, professional: true },
  { modelName: 'Gemini 1.5 Flash', basic: true, starter: true, professional: true },
  { modelName: 'Gemini 1.5 Pro', basic: false, starter: true, professional: true },
  { modelName: 'Gemini 2.0 Flash', basic: true, starter: true, professional: true },
  { modelName: 'Gemini 2.5 Pro', basic: false, starter: true, professional: true },
  { modelName: 'GPT-3.5 Turbo', basic: true, starter: true, professional: true },
  { modelName: 'GPT-4.5 Preview', basic: false, starter: true, professional: true },
  { modelName: 'o1', basic: false, starter: true, professional: true },
  { modelName: 'o1 Mini', basic: true, starter: true, professional: true },
  { modelName: 'o1 Pro', basic: false, starter: true, professional: true },
  { modelName: 'o3 Mini', basic: false, starter: true, professional: true },
  { modelName: 'GPT-4o', basic: false, starter: true, professional: true },
  { modelName: 'DeepSeek-R1', basic: true, starter: true, professional: true },
  { modelName: 'DeepSeek-R1 Llama 70B', basic: false, starter: true, professional: true },
  { modelName: 'DeepSeek-R1 Llama 8B', basic: true, starter: true, professional: true },
  { modelName: 'DeepSeek-R1 Qwen 14B', basic: true, starter: true, professional: true },
  { modelName: 'Grok-2 (Text Input)', basic: false, starter: true, professional: true },
];

// Fallback FAQ data
export const faqData = [
  {
    question: 'What is Krix AI?',
    answer: 'Krix AI is a platform that gives you access to 20+ LLM models from providers like OpenAI, Claude, Gemini, and DeepSeek through a single subscription. Switch between models, compare responses, and maintain unified conversation history.',
  },
  {
    question: 'How do compute credits work?',
    answer: 'Compute credits are used when you interact with LLM models. Different models consume different amounts of credits based on their complexity and capabilities. Each plan comes with a monthly allocation of compute credits.',
  },
  {
    question: 'Can I upgrade or downgrade my plan?',
    answer: 'Yes, you can upgrade or downgrade your plan at any time. When you upgrade, you\'ll get immediate access to the new features and additional compute credits.',
  },
  {
    question: 'What happens if I run out of compute credits?',
    answer: 'If you run out of compute credits, you can purchase additional credits or upgrade to a higher plan with more credits. You can also wait until your monthly allocation refreshes.',
  },
  {
    question: 'How long is the introductory offer available?',
    answer: 'Our special launch pricing is available for a limited time only. The countdown on our site shows the exact time remaining before prices increase.',
  },
  {
    question: 'Is there a free trial?',
    answer: 'We currently don\'t offer a free tier, but our plans are priced competitively with a significant discount during our launch period.',
  },
];
